import { useOutsideClick, useScreenSize } from '@hooks';
import { Button, Flex, Image, theme, Typography } from '@styles';
import {
  arrayOf,
  bool,
  func,
  instanceOf,
  object,
  shape,
  string,
} from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Modal } from '../Modal';

const DropdownItem = ({ id, label, onClick }) => (
  <Flex
    borderLeftColor={theme.colors.white}
    borderLeftStyle="solid"
    borderLeftWidth={2}
    height="56px"
    pl={3.5}
    width="100%"
  >
    <Button
      border={0}
      borderRadius={0}
      height="100%"
      justifyContent="flex-start"
      onClick={onClick(id)}
      pl={16}
      pr={24}
      variant="quaternary"
      width="100%"
    >
      <Typography
        color={theme.colors.white}
        textAlign="left"
        variant="MS-regular-s16-lh25"
      >
        {label}
      </Typography>
    </Button>
  </Flex>
);

const Dropdown = ({
  containerProps = {},
  excludedRef,
  isVisible,
  items,
  onClick,
  setIsVisible,
  userHeader,
}) => {
  const ref = useRef(null);
  const { isTabletLG } = useScreenSize();
  const [contentHeight, setContentHeight] = useState(0);
  const contentRef = useRef(null);
  useEffect(() => {
    setContentHeight(contentRef.current?.clientHeight);
  }, []);

  const handleOutsideClick = () => {
    setIsVisible?.(false);
  };
  useOutsideClick(ref, handleOutsideClick, excludedRef);

  if (isTabletLG) {
    return (
      <Modal
        isVisible={isVisible}
        justifyContent="center"
        modalContainerProps={{
          backgroundColor: 'transparent',
          borderRadius: '0px',
          height: '100%',
          maxHeight: '100%',
          width: '100%',
        }}
        modalMaxHeight="100%"
        setModalVisible={setIsVisible}
        withBackdropNoise
        withCloseIcon={false}
      >
        <Flex
          ref={ref}
          backdropFilter="blur(17px)"
          bg={theme.colors.white[10]}
          border={1}
          borderColor={theme.colors.white[10]}
          borderStyle="solid"
          borderWidth={1}
          bottom={0}
          flexDirection="column"
          p={16}
          position="absolute"
          width="100%"
          zIndex={2}
          {...containerProps}
        >
          {userHeader && (
            <Flex alignItems="center" gap={24} pb={32} pt={16}>
              <Image
                alt="Profile picture"
                borderRadius={20}
                height={40}
                src={userHeader?.avatar}
                width={40}
              />
              <Typography
                color={theme.colors.white[100]}
                variant="MS-semibold-s18-lh19"
              >
                @{userHeader?.username}
              </Typography>
            </Flex>
          )}
          {items?.map((item) => (
            <DropdownItem
              key={item.id}
              id={item.id}
              label={item.label}
              onClick={onClick}
            />
          ))}
        </Flex>
      </Modal>
    );
  }

  return (
    <Flex
      ref={ref}
      position="absolute"
      width="fit-content"
      zIndex={2}
      {...containerProps}
    >
      <div
        style={{
          height: isVisible ? contentHeight : 0,
          opacity: isVisible ? 1 : 0,
          overflow: 'hidden',
          transition: '0.5s ease-in-out',
          width: 'fit-content',
        }}
      >
        <Flex
          ref={contentRef}
          backdropFilter="blur(17px)"
          bg={theme.colors.white[10]}
          border={1}
          borderColor={theme.colors.white[10]}
          borderStyle="solid"
          borderWidth={1}
          flexDirection="column"
          p={16}
          {...containerProps}
        >
          {items?.map((item) => (
            <DropdownItem
              key={item.id}
              id={item.id}
              label={item.label}
              onClick={onClick}
            />
          ))}
        </Flex>
      </div>
    </Flex>
  );
};

DropdownItem.propTypes = {
  id: string.isRequired,
  label: string.isRequired,
  onClick: func.isRequired,
};

Dropdown.propTypes = {
  containerProps: object,
  excludedRef: shape({ current: instanceOf(Element) }),
  isVisible: bool.isRequired,
  items: arrayOf(
    shape({
      id: string,
      label: string,
    })
  ).isRequired,
  onClick: func.isRequired,
  setIsVisible: func.isRequired,
  userHeader: object,
};

Dropdown.defaultProps = {
  containerProps: {},
  excludedRef: null,
  userHeader: null,
};

export default Dropdown;
